import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, ImagePlus, Trash, X } from "lucide-react";
import { api, apiError, mediaUrl } from "@/lib/api";
import { fmtDate, fmtGNF } from "@/lib/format";
import { ABtn, AInput, ALabel, ASelect, Panel } from "./AdminLayout";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLES = {
  "en attente": "border-amber-700 bg-amber-950 text-amber-300",
  "validé": "border-emerald-700 bg-emerald-950 text-emerald-300",
  "refusé": "border-rose-800 bg-rose-950 text-rose-300",
};

const EMPTY = { product_id: "", quantity: 1, reason_category: "casse", comment: "", images: [] };

export function StockLosses({ products, onChanged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState({ items: [], pending: 0, reasons: [], total_value: 0, total_quantity: 0 });
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get("/warehouse/losses");
      setData(d);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    setForm((f) => (f.product_id ? f : { ...f, product_id: products[0]?.id || "" }));
  }, [products]);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: r } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...f.images, r.url] }));
      toast.success("Photo ajoutée");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data: created } = await api.post("/warehouse/losses", { ...form, quantity: Number(form.quantity) });
      toast.success(
        created.status === "validé"
          ? "Perte enregistrée : stock déduit de l'inventaire"
          : "Déclaration envoyée : en attente de validation par l'administrateur"
      );
      setForm({ ...EMPTY, product_id: form.product_id });
      load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const decide = async (loss, action) => {
    try {
      await api.post(`/warehouse/losses/${loss.id}/${action}`);
      toast.success(action === "validate" ? "Perte validée et stock déduit" : "Déclaration refusée");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]" data-testid="stock-losses-section">
      <Panel title="Casse / perte d'articles" subtitle="Article cassé, périmé, volé ou introuvable" testid="loss-form-panel">
        <div className="space-y-3">
          <div>
            <ALabel>Article *</ALabel>
            <ASelect value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} data-testid="loss-product-select">
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.sku} — {p.name} (stock {p.stock})
                </option>
              ))}
            </ASelect>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <ALabel>Quantité *</ALabel>
              <AInput type="number" min={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="loss-quantity-input" />
            </div>
            <div>
              <ALabel>Motif *</ALabel>
              <ASelect value={form.reason_category} onChange={(e) => setForm({ ...form, reason_category: e.target.value })} data-testid="loss-reason-select">
                {data.reasons.map((r) => (
                  <option key={r.id} value={r.id}>{r.label}</option>
                ))}
              </ASelect>
            </div>
          </div>
          <div>
            <ALabel>Commentaire</ALabel>
            <AInput value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="Carton tombé au déchargement…" data-testid="loss-comment-input" />
          </div>
          <div>
            <ALabel>Photos (optionnel)</ALabel>
            <div className="mt-1 flex flex-wrap gap-2">
              {form.images.map((img, i) => (
                <div key={i} className="relative h-20 w-20 overflow-hidden rounded-xl border border-slate-700">
                  <img src={mediaUrl(img)} alt="" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, images: form.images.filter((_, j) => j !== i) })}
                    className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-rose-600 text-white"
                    data-testid={`loss-remove-image-${i}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <label className="grid h-20 w-20 cursor-pointer place-items-center rounded-xl border border-dashed border-slate-600 text-slate-400 hover:bg-slate-800">
                <ImagePlus className="h-5 w-5" />
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="loss-image-input" />
              </label>
            </div>
          </div>
          <ABtn onClick={submit} disabled={busy || !form.product_id} className="w-full" data-testid="loss-submit-button">
            <Trash className="h-4 w-4" /> Déclarer la perte
          </ABtn>
          <p className="rounded-xl border border-amber-900/60 bg-amber-950/30 p-3 text-xs text-amber-200" data-testid="loss-note">
            {isAdmin
              ? "En tant qu'administrateur, votre déclaration est validée immédiatement et le stock est déduit."
              : "Les déclarations de l'entrepôt doivent être validées par l'administrateur avant déduction du stock."}
          </p>
        </div>
      </Panel>

      <Panel
        title="Historique des pertes"
        subtitle={`${data.pending} en attente · ${data.total_quantity} article(s) perdu(s) · ${fmtGNF(data.total_value)} de valeur d'achat`}
        testid="loss-list-panel"
      >
        <div className="space-y-2">
          {data.items.length === 0 && <p className="text-sm text-slate-500">Aucune perte déclarée.</p>}
          {data.items.map((l) => (
            <div key={l.id} className="rounded-xl border border-slate-800 p-3" data-testid={`loss-row-${l.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-semibold text-white">{l.product_name}</span>
                <span className={`ds-chip ${STATUS_STYLES[l.status] || ""}`} data-testid={`loss-status-${l.id}`}>{l.status}</span>
              </div>
              <p className="font-num mt-1 text-xs text-slate-300">
                {l.sku} · × {l.quantity} · {l.reason_label} · {fmtGNF(l.value)}
              </p>
              <p className="mt-1 text-[11px] text-slate-500">
                Déclaré par {l.created_by} · {fmtDate(l.created_at)}
                {l.validated_by ? ` · traité par ${l.validated_by}` : ""}
                {l.comment ? ` · ${l.comment}` : ""}
              </p>
              {(l.images || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {l.images.map((img, i) => (
                    <a key={i} href={mediaUrl(img)} target="_blank" rel="noopener noreferrer" data-testid={`loss-photo-${l.id}-${i}`}>
                      <img src={mediaUrl(img)} alt="" className="h-16 w-16 rounded-lg border border-slate-700 object-cover" />
                    </a>
                  ))}
                </div>
              )}
              {isAdmin && l.status === "en attente" && (
                <div className="mt-2 flex gap-2">
                  <ABtn onClick={() => decide(l, "validate")} data-testid={`loss-validate-${l.id}`}>
                    <Check className="h-3.5 w-3.5" /> Valider
                  </ABtn>
                  <ABtn variant="danger" onClick={() => decide(l, "reject")} data-testid={`loss-reject-${l.id}`}>
                    <X className="h-3.5 w-3.5" /> Refuser
                  </ABtn>
                </div>
              )}
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
